/**
 * DashboardOptions — side-by-side, switchable gallery of 4 DISTINCT central
 * dashboard layouts so the owner can feel what's missing by comparing.
 * Route: /dashboard-options   (interactive, sample data, no auth/backend)
 *
 * All four share the same sample data + the same Start/Import wizard
 * (imported from CentralDashboardPreview) so only the LAYOUT differs.
 *
 *  A · Command      — the current composite (baseline)
 *  B · Today        — task-first action feed, minimal chrome
 *  C · Cockpit      — numbers-forward financial KPIs + rich project rows
 *  D · Pipeline     — lifecycle kanban board (Setup→Bidding→Building→Ready→Done)
 */
import React, { useState } from 'react';
import {
  Plus, ArrowRight, Activity, Shield, Target, Building2, ChevronRight,
  CheckCircle2, Circle, Clock, AlertTriangle, FileText, Users, Sparkles, Rocket,
  ListChecks, DollarSign, Bell, Wallet, ArrowRightLeft, TrendingUp, Calendar,
  Gavel, Hammer, Flag, Layers, PiggyBank,
} from 'lucide-react';
import { StartProjectWizard } from './CentralDashboardPreview';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const ACTIVE = [
  { id: 1, name: 'Maple Ridge Townhomes', addr: 'Cedar Rapids, IA', drawn: 41, loan: 343000, avail: 202000, gaps: 0, stage: 'building', bankReady: true, interest: 28.4 },
  { id: 2, name: 'Oakwood Infill', addr: 'Iowa City, IA', drawn: 12, loan: 210000, avail: 184000, gaps: 2, stage: 'building', bankReady: false, interest: 15.6 },
  { id: 3, name: 'Ellis Row', addr: '204 Ellis Blvd', drawn: 0, loan: 0, avail: 0, gaps: 0, stage: 'setup', setup: true, interest: 0 },
  { id: 4, name: 'Grant St Duplex', addr: 'Cedar Rapids, IA', drawn: 0, loan: 0, avail: 0, gaps: 0, stage: 'bidding', interest: 0 },
];
const COMPLETED = [
  { id: 9, name: 'Cedar Court Duplex', addr: 'Marion, IA', drawn: 100, loan: 268000, stage: 'done' },
  { id: 10, name: 'Birchwood Flats', addr: 'Hiawatha, IA', drawn: 100, loan: 412000, stage: 'done' },
];

// ranked cross-project actions (used by Today + others)
const TASKS = [
  { icon: DollarSign, tone: 'green', t: 'Submit Maple Ridge draw #5', s: '3 phases bank-ready · $58K', proj: 'Maple Ridge', when: 'Today', cta: 'Request draw' },
  { icon: AlertTriangle, tone: 'amber', t: 'Chase 2 missing contractor W-9s', s: 'Blocks next payment', proj: 'Oakwood Infill', when: 'Today', cta: 'Send reminder' },
  { icon: FileText, tone: 'blue', t: 'Finish loan setup', s: 'Add terms + build schedule', proj: 'Ellis Row', when: 'This week', cta: 'Continue setup' },
  { icon: Gavel, tone: 'slate', t: 'Compare 3 bids for Electrical', s: 'Bids due 3 days ago', proj: 'Grant St Duplex', when: 'Overdue', cta: 'Review bids' },
];
const TONE = {
  green: 'bg-emerald-50 text-emerald-700', amber: 'bg-amber-50 text-amber-700',
  blue: 'bg-blue-50 text-blue-700', slate: 'bg-slate-100 text-slate-600',
};
const TONE_TXT = { green: 'text-emerald-600', amber: 'text-amber-600', blue: 'text-blue-600', slate: 'text-slate-500' };

// ------------------------------------------------- shared bits
const ProjRow = ({ p }) => (
  <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
        {p.name}
        {p.setup && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">setup</span>}
        {p.gaps > 0 && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{p.gaps} W-9</span>}
      </p>
      <p className="text-[11px] text-slate-400">{p.addr}</p>
    </div>
    <div className="hidden sm:flex items-center gap-2 w-40">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${p.drawn}%`, background: GREEN }} /></div>
      <span className="text-[11px] text-slate-400 w-8 text-right">{p.drawn}%</span>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-300" />
  </div>
);

const ActionBar = ({ openWizard }) => (
  <div className="flex items-center gap-2">
    <button onClick={openWizard} className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-slate-50" data-testid="opt-import-btn">
      <ArrowRightLeft className="w-4 h-4" /> Import existing project
    </button>
    <button onClick={openWizard} className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:shadow-lg transition" style={{ background: GREEN }} data-testid="opt-start-btn">
      <Plus className="w-4 h-4" /> Start a project
    </button>
  </div>
);

// ================================================= A · COMMAND (baseline)
const LayoutCommand = ({ openWizard }) => (
  <div className="space-y-5" data-testid="layout-command">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <p className="text-lg font-extrabold text-slate-900">Good morning, Jordan</p>
      <ActionBar openWizard={openWizard} />
    </div>
    <div className="rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }}>
      <div className="absolute -right-8 -top-10 w-44 h-44 rounded-full opacity-20" style={{ background: GREEN }} />
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Your next step</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">Request Maple Ridge draw #5</h2>
          <p className="text-blue-100 mt-2 text-sm max-w-lg">41% drawn · $202K available · 3 phases bank-ready.</p>
        </div>
        <button className="inline-flex items-center gap-2 bg-white text-sm font-bold px-5 py-3 rounded-xl" style={{ color: NAVY }}>Request draw <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><ListChecks className="w-4 h-4" style={{ color: GREEN }} /> Finish setting up Ellis Row</p>
            <span className="text-xs font-semibold text-slate-500">40% done</span>
          </div>
          {[['Create project', true], ['Add loan details', true], ['Invite contractors', false, true], ['Build draw schedule', false], ['Request first draw', false]].map(([lbl, done, active], i) => (
            <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${active ? 'bg-blue-50 border border-blue-200' : ''}`}>
              {done ? <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} /> : active ? <Clock className="w-4 h-4 text-blue-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
              <span className={`text-sm flex-1 ${done ? 'text-slate-400 line-through' : active ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{lbl}</span>
              {active && <button className="text-xs font-bold text-white px-3 py-1 rounded-lg" style={{ background: NAVY }}>Continue</button>}
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> Active projects</div>
          {ACTIVE.map((p) => <ProjRow key={p.id} p={p} />)}
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-2xl p-5 text-white" style={{ background: NAVY }}>
          <p className="text-[11px] uppercase tracking-wide font-bold text-blue-200">Portfolio</p>
          <p className="text-3xl font-extrabold mt-1">$386K</p>
          <p className="text-xs text-blue-200">available to draw</p>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Total loans</span><span className="font-semibold">$553K</span></div>
            <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Daily interest</span><span className="font-semibold">$49.52</span></div>
            <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Bank-ready</span><span className="font-semibold">80 / 100</span></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: GREEN }} /> Needs attention</p>
          {TASKS.slice(0, 3).map((t, i) => (
            <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
              <t.icon className={`w-4 h-4 ${TONE_TXT[t.tone]} flex-shrink-0`} />
              <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-slate-800 truncate">{t.t}</p><p className="text-[10px] text-slate-400">{t.proj}</p></div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ================================================= B · TODAY (action feed)
const LayoutToday = ({ openWizard }) => {
  const groups = ['Overdue', 'Today', 'This week'];
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="layout-today">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-2xl font-extrabold text-slate-900">Today</p>
          <p className="text-sm text-slate-500">4 things need you across 3 projects · $386K available to draw</p>
        </div>
        <ActionBar openWizard={openWizard} />
      </div>
      {groups.map((g) => {
        const items = TASKS.filter((t) => t.when === g);
        if (!items.length) return null;
        return (
          <div key={g}>
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${g === 'Overdue' ? 'text-red-500' : g === 'Today' ? 'text-slate-800' : 'text-slate-400'}`}>{g}</p>
            <div className="space-y-2">
              {items.map((t, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-sm transition">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${TONE[t.tone]}`}><t.icon className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{t.t}</p>
                    <p className="text-xs text-slate-500">{t.proj} · {t.s}</p>
                  </div>
                  <button className="text-xs font-bold text-white px-4 py-2 rounded-xl flex-shrink-0" style={{ background: t.when === 'Overdue' ? '#dc2626' : NAVY }}>{t.cta}</button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 flex items-center justify-between">
        <p className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> All caught up beyond this. 2 active builds running smoothly.</p>
        <button className="text-xs font-semibold text-blue-600">View all projects →</button>
      </div>
    </div>
  );
};

// ================================================= C · COCKPIT (numbers-forward)
const LayoutCockpit = ({ openWizard }) => {
  const kpis = [
    { icon: Wallet, l: 'Available to draw', v: '$386K', tone: 'green' },
    { icon: PiggyBank, l: 'Total loans out', v: '$553K', tone: 'blue' },
    { icon: Activity, l: 'Daily interest', v: '$49.52', tone: 'amber' },
    { icon: TrendingUp, l: 'Drawn this month', v: '$127K', tone: 'blue' },
    { icon: Target, l: 'Bank-ready score', v: '80', tone: 'green' },
  ];
  return (
    <div className="space-y-5" data-testid="layout-cockpit">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-lg font-extrabold text-slate-900">Portfolio cockpit</p>
        <ActionBar openWizard={openWizard} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <div key={k.l} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${TONE[k.tone]}`}><k.icon className="w-4.5 h-4.5" /></div>
            <p className="text-2xl font-extrabold text-slate-900 mt-3">{k.v}</p>
            <p className="text-[11px] text-slate-500">{k.l}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm font-bold text-slate-700">Active builds</p>
          <span className="text-[11px] text-slate-400">drawn / available / interest·day</span>
        </div>
        {ACTIVE.filter((p) => !p.setup && p.loan > 0).map((p) => (
          <div key={p.id} className="px-5 py-4 border-b border-slate-50 last:border-0 hover:bg-slate-50">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-slate-900">{p.name}</p>
                {p.bankReady && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">bank-ready</span>}
                {p.gaps > 0 && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{p.gaps} W-9</span>}
              </div>
              <div className="hidden sm:flex items-center gap-6 text-right">
                <div><p className="text-sm font-bold text-slate-900">{p.drawn}%</p><p className="text-[10px] text-slate-400">drawn</p></div>
                <div><p className="text-sm font-bold text-slate-900">${(p.avail / 1000).toFixed(0)}K</p><p className="text-[10px] text-slate-400">available</p></div>
                <div><p className="text-sm font-bold text-amber-600">${p.interest.toFixed(2)}</p><p className="text-[10px] text-slate-400">int/day</p></div>
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-2 rounded-full" style={{ width: `${p.drawn}%`, background: GREEN }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================= D · PIPELINE (kanban)
const LayoutPipeline = ({ openWizard }) => {
  const cols = [
    { key: 'setup', label: 'Setup', icon: FileText, color: '#f59e0b' },
    { key: 'bidding', label: 'Bidding', icon: Gavel, color: '#2563eb' },
    { key: 'building', label: 'Building', icon: Hammer, color: GREEN },
    { key: 'done', label: 'Complete', icon: Flag, color: '#64748b' },
  ];
  const all = [...ACTIVE, ...COMPLETED];
  return (
    <div className="space-y-5" data-testid="layout-pipeline">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-lg font-extrabold text-slate-900 flex items-center gap-2"><Layers className="w-5 h-5 text-slate-400" /> Deal pipeline</p>
          <p className="text-sm text-slate-500">Every property by stage · drag to advance</p>
        </div>
        <ActionBar openWizard={openWizard} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cols.map((c) => {
          const items = all.filter((p) => p.stage === c.key);
          return (
            <div key={c.key} className="rounded-2xl bg-slate-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color: c.color }}><c.icon className="w-3.5 h-3.5" /> {c.label}</p>
                <span className="text-[11px] font-bold text-slate-400 bg-white rounded-full px-2">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {items.map((p) => (
                  <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm hover:shadow-md transition cursor-pointer">
                    <p className="text-sm font-bold text-slate-900 truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-400">{p.addr}</p>
                    {p.loan > 0 && (
                      <div className="mt-2">
                        <div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${p.drawn}%`, background: c.color }} /></div>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-slate-500">${(p.loan / 1000).toFixed(0)}K loan</span>
                          {p.gaps > 0 && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 rounded">{p.gaps} W-9</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {c.key === 'setup' && (
                  <button onClick={openWizard} className="w-full rounded-xl border-2 border-dashed border-slate-300 py-3 text-xs font-semibold text-slate-400 hover:border-emerald-400 hover:text-emerald-600 flex items-center justify-center gap-1"><Plus className="w-3.5 h-3.5" /> Add property</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================= page shell + switcher
const OPTIONS = [
  { key: 'command', label: 'A · Command', sub: 'Next-step + setup + portfolio (current)', Comp: LayoutCommand },
  { key: 'today', label: 'B · Today', sub: 'Task-first action feed, minimal chrome', Comp: LayoutToday },
  { key: 'cockpit', label: 'C · Cockpit', sub: 'Numbers-forward KPIs + financial rows', Comp: LayoutCockpit },
  { key: 'pipeline', label: 'D · Pipeline', sub: 'Lifecycle kanban board', Comp: LayoutPipeline },
];

export default function DashboardOptions() {
  const [opt, setOpt] = useState('command');
  const [wizard, setWizard] = useState(false);
  const current = OPTIONS.find((o) => o.key === opt);
  const Comp = current.Comp;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* preview banner */}
      <div className="text-center text-[11px] font-semibold text-amber-900 bg-amber-300 py-1.5 px-4">
        PREVIEW · compare 4 dashboard layouts · sample data
      </div>
      {/* top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-end gap-[2px]"><div className="w-2 h-6 rounded-sm" style={{ background: GREEN }} /><div className="w-2 h-6 rounded-sm -ml-[2px]" style={{ background: GREEN }} /></div>
            <span className="font-extrabold tracking-tight" style={{ color: NAVY }}>DrawReady</span>
            <span className="text-xs text-slate-400 hidden sm:inline">· dashboard options</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative text-slate-400 hover:text-slate-700"><Bell className="w-5 h-5" /><span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: GREEN }}>3</span></button>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: NAVY }}>JD</div>
          </div>
        </div>
      </div>

      {/* option switcher */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="option-switcher">
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              onClick={() => setOpt(o.key)}
              data-testid={`opt-tab-${o.key}`}
              className={`text-left rounded-xl border-2 px-4 py-3 transition ${opt === o.key ? 'bg-white shadow-sm' : 'bg-white/60 border-transparent hover:bg-white'}`}
              style={opt === o.key ? { borderColor: NAVY } : {}}
            >
              <p className="text-sm font-bold" style={{ color: opt === o.key ? NAVY : '#334155' }}>{o.label}</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{o.sub}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Comp openWizard={() => setWizard(true)} />
        <p className="text-center text-[11px] text-slate-400 pt-8 pb-6">
          Switch tabs above to compare. Every layout shares the same data + the same <b>Start / Import</b> wizard.
        </p>
      </div>

      {wizard && <StartProjectWizard onClose={() => setWizard(false)} />}
    </div>
  );
}
